import React from "react";
import { FieldGrounding } from "../../../models/intake";
import "./QuickEntryGroundingIcon.scss";

// AB#35066. Per-VALUE "grounding" affordance for the Quick Entry row list: a small
// clickable "document lines" (≡) glyph rendered immediately to the RIGHT of a
// DOC-SOURCED value (see master-demo-v6.html's own .qe-row__inline-ground). It sits
// inline next to each value (including per-sub-value on the composite Request
// Type/Purpose row). (The earlier per-ROW confidence dot + "Source" text button in the
// row-adornment slot was removed - confidence moved to the left row-status icon and
// grounding to this inline glyph.)
//
// Callers gate rendering on "value is doc-sourced" (i.e. equals the AI-extracted value,
// unedited) so the icon never shows for a manually-entered/edited value. The click is
// wired to the SAME scroll-to-source path the row-level affordance uses
// (DataEntryDetails.onRevealQuickEntryGrounding); where the extraction produced no
// grounding for this field (e.g. Service Date, or Intake-side locatedGate OFF) the
// button still renders and clicking degrades to the existing informational no-source
// handler (onUnavailable) - it is always a real, focusable, keyboard-operable
// <button>, never a dead/inert element and never a hard error.
//
// AB#38128 narrowed that caller contract, and this component was deliberately left
// unchanged. The row-generic caller (DataEntryDetails.renderRowContent) now ALSO
// requires that the field be groundable at all - either in the producer's located-field
// scope, or carrying a live FieldGrounding in this render's payload. So it can no longer
// pass a null `field`, and the four rows that could only ever produce the no-source
// variant (Records Requested, Sensitive Information, Received Date, Due Date) render no
// glyph rather than one that denies the document is the source.
//
// The null-field and no-grounding paths below are still exercised by this component's own
// tests and are still correct for the four call sites that pass string literals; they are
// simply no longer reachable from the row-generic caller. Do not "restore" the dead
// affordance on those rows by reverting the caller's second term.

/** Pure lookup: the coordinate grounding for `field`, or undefined when absent. */
export function findFieldGrounding(
    fieldGroundings: readonly FieldGrounding[] | undefined,
    field: string | null,
): FieldGrounding | undefined {
    if (!field || !fieldGroundings) {
        return undefined;
    }
    return fieldGroundings.find(g => g.field === field);
}

export interface QuickEntryGroundingIconProps {
    /**
     * camelCase RequestAnalysisData field key this value grounds to (e.g.
     * "requestType", "infoReleaseSections"). null when the value has no single
     * mappable grounding key - the button still renders and clicking degrades to
     * onUnavailable (graceful, never a throw). Per AB#38128 the row-generic caller no
     * longer passes null here; the remaining callers pass string literals.
     *
     * AB#35422: the two Service Date rows used to be the example of a null key.
     * They now resolve to "serviceDateFrom"/"serviceDateTo" - the retriever
     * translates the contract's indexed ServiceDates[i].From/.To names onto those
     * fixed keys for whichever range the panel displays.
     */
    readonly field: string | null;
    /** Human field/value name for the accessible label, e.g. "Service Date — From". */
    readonly label: string;
    readonly fieldGroundings?: readonly FieldGrounding[];
    /** Reuse of DataEntryDetails.onRevealQuickEntryGrounding (scroll viewer to source). */
    readonly onReveal?: (grounding: FieldGrounding) => void;
    /** Reuse of DataEntryDetails.onQuickEntryGroundingUnavailable (informational only). */
    readonly onUnavailable?: () => void;
}

/**
 * Clickable ≡ grounding icon for one doc-sourced Quick Entry value.
 *
 * It is a real <button>, so Enter/Space activation is native - deliberately NOT the
 * onMouseDown-only path AB#35067 had to fix elsewhere. onClick handles mouse AND
 * keyboard, and always resolves to a defined handler (reveal when grounded, otherwise
 * the informational no-source handler), so it is never inert.
 */
const QuickEntryGroundingIcon: React.FC<QuickEntryGroundingIconProps> = (props) => {
    const grounding = findFieldGrounding(props.fieldGroundings, props.field);
    const onActivate = () => (grounding ? props.onReveal?.(grounding) : props.onUnavailable?.());

    // AB#35066: the two states must be distinguishable to a SCREEN READER too, not only visually.
    // Both used to announce "Show X in the document" while only `title` differed - and aria-label
    // wins over title, so a screen-reader user got no warning at all before clicking and hearing
    // that no source exists. The PRD asks for a distinct no-source "variant"; the accessible name
    // is half of that, and arguably the half that matters more.
    return (
        <button
            type="button"
            className={`qe-row__inline-ground ${grounding ? "has-grounding" : "no-grounding"}`}
            data-ground-target={props.field ?? undefined}
            data-testid={`inline-ground-${props.field ?? props.label}`}
            aria-label={grounding
                ? `Show ${props.label} in the document`
                : `No source location in the document for ${props.label}`}
            title={grounding ? `Show ${props.label} in the document` : "Source location not available yet"}
            onClick={onActivate}
        >
            <svg aria-hidden="true" focusable="false" width="14" height="14" viewBox="0 0 14 14">
                <line x1="2" y1="4" x2="12" y2="4" />
                <line x1="2" y1="7.5" x2="10" y2="7.5" />
                <line x1="2" y1="11" x2="7" y2="11" />
                {/* AB#35066: the PRD specifies the no-source state as a distinct icon VARIANT ("with
                    a slash"), but the two states differed only by a shade of grey (#9aa0a6 vs
                    #bfc4c9) on an identical glyph — indistinguishable at 14px, which is why QA read
                    a documented state as a broken icon. The diagonal makes the variant actually
                    visible. Inherits the same stroke styling as the lines above; the class exists so
                    the slash can be tuned (e.g. a knockout) without touching this markup. */}
                {!grounding ? <line className="qe-row__ground-slash" x1="2.5" y1="11.5" x2="11.5" y2="2.5" /> : null}
            </svg>
        </button>
    );
};

export default QuickEntryGroundingIcon;
